import { LabLocalStage, LabProgress } from './labSdk.types'
import { labStageFraction, labStageRemainingMs, simulateLabStagePercent } from './labStageClock'
import { useEffect, useRef, useState } from 'react'

import { resolveLabStageIndex } from './labSdk.mapping'

const TICK_MS = 250
/** The live segment always shows it has started */
const MIN_LIVE_FILL = 0.08

/**
 * The client-side steps of the leading "Prepare" segment (no stage clock — the order does not exist yet): each owns a
 * share of the segment and eases across it on its typical duration, with the order stages' curve, holding before the
 * next step's share. A step that is skipped (a photo already on the Lab) simply jumps the fill forward.
 */
const PREPARE_STEPS: Record<string, { from: number, to: number, estMs: number }> = {
  [LabLocalStage.PREPARE]: { from: 0.04, to: 0.2, estMs: 1500 },
  [LabLocalStage.UPLOAD]: { from: 0.2, to: 0.5, estMs: 4000 },
  [LabLocalStage.UPLOAD_MASK]: { from: 0.5, to: 0.7, estMs: 3000 },
  [LabLocalStage.QUEUED]: { from: 0.7, to: 0.95, estMs: 6000 },
}

/** Without a Prepare segment (an order that starts with Develop), the client-side steps fill this much of Develop */
const MERGED_PREPARE_SHARE = 0.3

export interface LabProgressSegment {
  stage: string
  /** 0..1 */
  fill: number
  isCurrent: boolean
}

/**
 * What the generating overlay shows for a Lab order. Between real events the running stage advances on its own
 * clock (`labStageClock.ts`) — a cloud generation reports nothing until it is done — so this ticks while a clock is
 * running, inside the overlay only, rather than re-rendering the Studio. The live segment fills with the share of
 * its stage done; without a clock it falls back to the overall percentage. Before the order runs, the "Prepare"
 * segment advances step by step (PREPARE_STEPS) and the message names the step. Nothing moves backwards: the
 * percentage, the stage and every segment only grow.
 */
export const useLabProgressDisplay = (progress: LabProgress, plan: string[]) => {
  const { clock } = progress
  const [now, setNow] = useState(() => Date.now())
  const peakRef = useRef<{ percent: number, stageIndex: number, fills: number[] }>({ percent: 0, stageIndex: 0, fills: [] })
  const prepareStepRef = useRef<{ key: string, startedAt: number } | null>(null)

  const peak = peakRef.current
  const stageIndex = plan.length > 0 ? Math.min(plan.length - 1, Math.max(peak.stageIndex, resolveLabStageIndex(progress.stage, plan, progress.percent))) : 0
  // The client-side step running now (the SDK label as received), timed from when it first showed
  const rawStage = progress.rawStage ?? progress.stage
  // A plan that starts with Develop has no Prepare segment (withLabPrepareStep): its client-side steps run inside Develop
  const hasPrepareSegment = plan[0] === LabLocalStage.PREPARE
  const isPreparing = hasPrepareSegment
    ? plan[stageIndex] === LabLocalStage.PREPARE
    : plan.length > 0 && stageIndex === 0 && Boolean(PREPARE_STEPS[rawStage])
  const prepareKey = isPreparing ? (PREPARE_STEPS[rawStage] ? rawStage : LabLocalStage.PREPARE) : null
  if (prepareKey && prepareStepRef.current?.key !== prepareKey) prepareStepRef.current = { key: prepareKey, startedAt: Date.now() }

  const simulated = clock ? simulateLabStagePercent(clock, now) : 0
  const remainingMs = clock ? labStageRemainingMs(clock, now) : progress.etaMs
  const isAdvancing = isPreparing || (!!clock && (simulated < clock.ceil - 1 || (remainingMs ?? 0) > 0))

  useEffect(() => {
    if (!isAdvancing) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(timer)
  }, [isAdvancing])

  const percent = Math.max(peak.percent, progress.percent, simulated)
  // The order's percentage spreads over its own stages, not over the client-side Prepare step
  const offset = hasPrepareSegment ? 1 : 0
  const perSegment = 100 / Math.max(1, plan.length - offset)
  const prepareFill = () => {
    const step = PREPARE_STEPS[prepareKey ?? LabLocalStage.PREPARE]
    const elapsed = Math.max(0, now - (prepareStepRef.current?.startedAt ?? now))
    const fill = step.from + (step.to - step.from) * (1 - Math.exp(-elapsed / (step.estMs * 0.7)))
    // Inside Develop the steps take its first part only, leaving the rest to the order's own Develop work
    return hasPrepareSegment ? fill : fill * MERGED_PREPARE_SHARE
  }
  const fills = plan.map((_, index) => {
    if (index < stageIndex) return 1
    if (index > stageIndex) return 0
    const live = isPreparing ? prepareFill() : clock ? labStageFraction(clock, percent) : (percent - (index - offset) * perSegment) / perSegment
    return Math.min(1, Math.max(MIN_LIVE_FILL, live, peak.fills[index] ?? 0))
  })
  peakRef.current = { percent, stageIndex, fills }

  return {
    percent,
    /** While preparing the percentage belongs to an order that has not started: the message names the step instead */
    isPreparing,
    /** The message key: the client-side step while preparing, else the plan stage (the SDK label when there is no plan) */
    stageKey: prepareKey ?? plan[stageIndex] ?? progress.stage,
    segments: plan.map((stage, index): LabProgressSegment => ({ stage, fill: fills[index], isCurrent: index === stageIndex })),
    remainingMs,
  }
}
